import { useCallback, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Camera, CheckCheck, ImageIcon, Loader2, PackageCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, productImage } from "@/lib/api";
import ImageLightbox from "@/components/ImageLightbox";
import { Button } from "@/components/ui/button";

export function FulfillmentToggle({ value, onChange, testid }) {
  const v = value || "PO";
  return (
    <div className="inline-flex shrink-0 rounded-md border bg-white p-0.5" data-testid={testid}>
      {["PO", "READY"].map((t) => (
        <button
          key={t}
          type="button"
          data-testid={`${testid}-${t.toLowerCase()}`}
          onClick={() => onChange(t)}
          className={`h-8 rounded px-2.5 text-[11px] font-bold tracking-wide transition-colors ${
            v === t ? (t === "READY" ? "bg-[#8FA3AD] text-white" : "bg-primary text-primary-foreground") : "text-stone-500 hover:bg-accent"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

export function ReadyCountLine({ po, ready, testid }) {
  if (!ready) return null;
  return (
    <p className="text-[11px] font-semibold tracking-wide text-[#6E8591]" data-testid={testid}>
      {po} PO · {ready} READY
    </p>
  );
}

export function ReadyItemBadge({ item }) {
  if (item.fulfillment_type !== "READY") return null;
  const label = item.release_required && !item.released_at
    ? "RELEASE NEEDED"
    : item.ready_check_status === "CHECKED" ? "READY · CHECKED" : item.ready_status === "KEPT" ? "READY · KEPT" : "READY · WAITING TO KEEP";
  return (
    <span data-testid={`ready-badge-${item.id}`} className="mt-0.5 inline-block rounded-md border border-[#C9D3D8] bg-[#EEF2F4] px-1.5 py-0.5 text-[10px] font-bold text-[#56707C]">
      {label}
    </span>
  );
}

function useReadyItems(view) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ready-items", { params: { view } });
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
      setRows([]);
    }
  }, [view]);
  useEffect(() => {
    load();
  }, [load]);
  return [rows, load];
}

function ReadyRowInfo({ r, extra }) {
  const img = productImage(r);
  return (
    <div className="flex items-center gap-3">
      {img ? (
        <img src={img} alt={r.product_name} className="h-14 w-14 shrink-0 rounded-md border bg-stone-100 object-cover" />
      ) : (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border bg-stone-100"><ImageIcon className="h-5 w-5 text-stone-300" /></div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{r.customer_name}</p>
        <p className="truncate text-xs text-muted-foreground">#{r.order_number} · {r.batch_name}</p>
        <p className="truncate text-sm">{r.product_name}{r.variant ? ` · ${r.variant}` : ""} <b>× {r.quantity}</b></p>
        {extra}
      </div>
    </div>
  );
}

function KeepRow({ r, onDone }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const confirm = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/ready-items/${r.id}/keep`, fd);
      toast.success(`${r.product_name} KEPT for ${r.customer_name}`);
      window.dispatchEvent(new Event("ht:ready-changed"));
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Photo upload failed — item not kept. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`ready-keep-row-${r.id}`} className="space-y-3 rounded-md border bg-card p-3">
      <ReadyRowInfo r={r} extra={<span className="mt-1 inline-block rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">WAITING TO KEEP</span>} />
      <p className="text-xs text-muted-foreground">Take the product, attach the customer name label, then photo both together.</p>
      {preview && <img src={preview} alt="Keep evidence" data-testid={`ready-keep-preview-${r.id}`} className="h-32 w-full rounded-md border object-cover" />}
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`ready-keep-photo-input-${r.id}`} onChange={(e) => pick(e.target.files?.[0])} />
      <div className="flex gap-2">
        <Button variant="outline" className="h-11 flex-1" data-testid={`ready-keep-photo-btn-${r.id}`} onClick={() => ref.current?.click()} disabled={busy}>
          <Camera className="h-4 w-4" /> {file ? "Retake" : "Take Photo"}
        </Button>
        <Button className="h-11 flex-1" data-testid={`ready-keep-confirm-${r.id}`} onClick={confirm} disabled={!file || busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} CONFIRM KEEP
        </Button>
      </div>
    </div>
  );
}

function ReleaseRow({ r, onDone }) {
  const [busy, setBusy] = useState(false);
  const release = async () => {
    setBusy(true);
    try {
      await api.post(`/ready-items/${r.id}/released`);
      toast.success("Marked as released back to stock");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`ready-release-row-${r.id}`} className="space-y-2 rounded-md border border-red-200 bg-red-50/40 p-3">
      <ReadyRowInfo r={r} extra={<span className="mt-1 inline-block text-[10px] font-bold text-red-600">CANCELLED AFTER KEEP — remove label & return to stock</span>} />
      <Button variant="outline" className="h-10 w-full" data-testid={`ready-release-btn-${r.id}`} onClick={release} disabled={busy}>
        <Undo2 className="h-4 w-4" /> Released to Stock
      </Button>
    </div>
  );
}

export function ReadyKeepSection() {
  const [rows, load] = useReadyItems("keep");
  const [release, loadRelease] = useReadyItems("release");
  if (rows === null) return null;
  return (
    <div className="mb-5 space-y-2" data-testid="ready-keep-section">
      <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">
        Ready to Keep <span className="ml-1 rounded-md border bg-card px-1.5 py-0.5 text-[11px] text-stone-600" data-testid="ready-keep-count">{rows.length}</span>
      </h2>
      {rows.length === 0 ? (
        <p data-testid="ready-keep-empty" className="rounded-md border border-dashed py-4 text-center text-sm text-muted-foreground">No READY products waiting to keep</p>
      ) : (
        rows.map((r) => <KeepRow key={r.id} r={r} onDone={load} />)
      )}
      {release?.length > 0 && release.map((r) => <ReleaseRow key={r.id} r={r} onDone={loadRelease} />)}
    </div>
  );
}

function CheckRow({ r, onDone, onZoom }) {
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ready-items/${r.id}/check`);
      toast.success(`Checked — ${r.product_name} for ${r.customer_name}`);
      if (data.packing_sync === "added") toast.info("Added to the order's open packing job");
      if (data.packing_sync === "flagged_locked_job") toast.warning("The order's packing job is locked — flagged for Owner to reopen and add this item");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`ready-check-row-${r.id}`} className="space-y-3 rounded-md border bg-card p-3">
      <ReadyRowInfo
        r={r}
        extra={<p className="text-[11px] text-muted-foreground">Kept by {r.kept_by_name} · {r.kept_at && format(new Date(r.kept_at), "d MMM, HH:mm")}</p>}
      />
      {r.keep_photo_path && (
        <button type="button" className="block w-full" data-testid={`ready-check-photo-${r.id}`} onClick={() => onZoom(fileUrl(r.keep_photo_path))}>
          <img src={fileUrl(r.keep_photo_path)} alt="Keep evidence" className="h-36 w-full rounded-md border object-cover" />
        </button>
      )}
      <Button className="h-11 w-full" data-testid={`ready-check-verify-${r.id}`} onClick={verify} disabled={busy}>
        <CheckCheck className="h-4 w-4" /> Verify READY Item
      </Button>
    </div>
  );
}

export function ReadyCheckSection() {
  const [rows, load] = useReadyItems("check");
  const [zoom, setZoom] = useState(null);
  if (rows === null) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="ready-check-section">
      <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">
        READY Items to Check <span className="ml-1 rounded-md border bg-card px-1.5 py-0.5 text-[11px] text-stone-600" data-testid="ready-check-count">{rows.length}</span>
      </h2>
      {rows.length === 0 ? (
        <p data-testid="ready-check-empty" className="rounded-md border border-dashed py-4 text-center text-sm text-muted-foreground">No kept READY items waiting for check</p>
      ) : (
        rows.map((r) => <CheckRow key={r.id} r={r} onDone={load} onZoom={setZoom} />)
      )}
      {zoom && <ImageLightbox src={zoom} alt="Keep evidence" onClose={() => setZoom(null)} />}
    </div>
  );
}
